import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ArrowDownAZ, ArrowUpAZ, Filter, Search, X } from 'lucide-react';
import { cn } from '@insolech/ui';

/**
 * ColumnFilterPopover - Excel-style header dropdown for the order tracker.
 *
 * Click the header → opens a popover with:
 *   - Sort A → Z / Z → A (or Oldest/Newest first for dates)
 *   - An input that narrows THE LIST OF VALUES below (not the orders)
 *   - Multi-select checklist (auto-applies; toggling a row updates the
 *     parent filter state immediately, no separate Apply button)
 *   - Clear filter / Select all
 *
 * Date columns pass `kind="date"` to render the sort buttons only.
 *
 * WHY THE VALUE INPUT IS CONDITIONAL. It used to render always, as a
 * magnifying-glass box reading "Search order ID" / "Search patient name" -
 * indistinguishable from record search, but it only ever matched the VALUE
 * LIST, and on Order ID that list was hard-coded empty so it answered "No
 * matches." for every order that has ever existed. It now appears only when
 * there are more values than anyone wants to scan, says that it filters the
 * list, and offers the page search when nothing matches. Columns whose job
 * the page search does properly pass no options at all.
 */
const VALUE_FILTER_MIN_OPTIONS = 12;

export interface ColumnFilterOption {
  value: string;
  label: string;
}

interface Props {
  /** Column display name (rendered as the trigger label). */
  label: string;
  /** Visual flavor for sort buttons. */
  kind?: 'text' | 'date' | 'number';
  /** Whether the column is the active sort. */
  sortActive: boolean;
  sortDirection: 'asc' | 'desc';
  onSortAsc: () => void;
  onSortDesc: () => void;
  /**
   * When omitted, the popover renders sort only (used for date / number-ish
   * columns where multi-select isn't useful).
   */
  options?: ColumnFilterOption[];
  /** Currently-selected filter values. Empty = no filter (everything passes). */
  selected?: string[];
  onSelectedChange?: (next: string[]) => void;
  /** Placeholder for the in-popover VALUE filter input. */
  searchPlaceholder?: string;
  /**
   * Where the values come from, said plainly under the list (e.g. "from the
   * orders loaded on this page"). Absent = no claim made.
   */
  scopeNote?: string;
  /**
   * Hands the typed text to the page-level search when no value matches, so
   * the wrong door opens the right one instead of reporting "No matches" for
   * a record that exists.
   */
  onGlobalSearch?: (term: string) => void;
  /** Rendered in place of the checklist when this column has no value filter. */
  sortOnlyHint?: React.ReactNode;
  /** Fired when the popover opens (used to widen the fetch behind the values). */
  onOpen?: () => void;
  /** Position of the trigger label relative to the popover content. */
  align?: 'start' | 'center' | 'end';
}

export function ColumnFilterPopover({
  label,
  kind = 'text',
  sortActive,
  sortDirection,
  onSortAsc,
  onSortDesc,
  options,
  selected = [],
  onSelectedChange,
  searchPlaceholder,
  scopeNote,
  onGlobalSearch,
  sortOnlyHint,
  onOpen,
  align = 'start',
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  // `options` UNDEFINED means "this column has no value filter" and renders
  // sort only. NOTE an empty array is still "has options" (a column can be
  // legitimately empty while the first page loads); what fixed the Order ID
  // dead end was removing the props at that call site, not this predicate.
  const hasOptions = Array.isArray(options) && !!onSelectedChange;
  const showValueFilter = hasOptions && (options as ColumnFilterOption[]).length >= VALUE_FILTER_MIN_OPTIONS;
  const filterActive = selected.length > 0;

  const filteredOptions = React.useMemo(() => {
    if (!options) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const toggle = (value: string) => {
    if (!onSelectedChange) return;
    if (selected.includes(value)) {
      onSelectedChange(selected.filter((v) => v !== value));
    } else {
      onSelectedChange([...selected, value]);
    }
  };

  const clearAll = () => onSelectedChange?.([]);
  const selectAllFiltered = () => {
    if (!onSelectedChange) return;
    const next = new Set([...selected, ...filteredOptions.map((o) => o.value)]);
    onSelectedChange(Array.from(next));
  };

  const sortAscLabel = kind === 'date' ? 'Oldest first' : kind === 'number' ? 'Smallest first' : 'Sort A → Z';
  const sortDescLabel = kind === 'date' ? 'Newest first' : kind === 'number' ? 'Largest first' : 'Sort Z → A';

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) onOpen?.();
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-text-secondary',
            // Mobile: pad the ~18px-tall trigger up to a usable touch target.
            'max-sm:py-2 max-sm:-my-2',
            'hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded',
            (sortActive || filterActive) && 'text-text-primary',
          )}
          aria-label={hasOptions ? `Filter or sort ${label}` : `Sort ${label}`}
        >
          <span>{label}</span>
          {filterActive ? (
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full bg-brand-500"
              title="Filter active"
            />
          ) : null}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={6}
          collisionPadding={12}
          // R28 follow-up - fit within viewport: Radix exposes the available
          // height (below or above the trigger) and width via CSS vars; we
          // cap the popover to that and let the checklist absorb the rest.
          style={{
            maxHeight: 'var(--radix-popover-content-available-height)',
            maxWidth: 'var(--radix-popover-content-available-width)',
          }}
          className={cn(
            'z-50 w-64 rounded-md border border-border-subtle bg-surface-elevated shadow-lg',
            'p-2 text-sm text-text-primary outline-none flex flex-col',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
        >
          {/* Sort row */}
          <div className="flex flex-col gap-1 pb-2 border-b border-border-subtle shrink-0">
            <SortButton
              label={sortAscLabel}
              icon={kind === 'text' ? <ArrowDownAZ className="h-3.5 w-3.5" /> : null}
              active={sortActive && sortDirection === 'asc'}
              onClick={() => {
                onSortAsc();
                setOpen(false);
              }}
            />
            <SortButton
              label={sortDescLabel}
              icon={kind === 'text' ? <ArrowUpAZ className="h-3.5 w-3.5" /> : null}
              active={sortActive && sortDirection === 'desc'}
              onClick={() => {
                onSortDesc();
                setOpen(false);
              }}
            />
          </div>

          {hasOptions ? (
            <>
              {/* Value filter, only for lists too long to scan. */}
              {showValueFilter ? (
              <div className="relative mt-2 mb-2 shrink-0">
                <Search
                  aria-hidden="true"
                  className="absolute start-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted"
                />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder ?? `Filter this list of ${label.toLowerCase()} values`}
                  className={cn(
                    'w-full h-8 ps-7 pe-2 rounded-md border border-border-subtle bg-surface-base',
                    'text-sm placeholder:text-text-muted',
                    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
                  )}
                />
              </div>
              ) : null}

              {/* Actions row */}
              <div className="flex items-center justify-between text-xs text-text-secondary mb-1 px-1 shrink-0">
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="hover:text-brand-600"
                >
                  {searchTerm ? 'Select matches' : 'Select all'}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className={cn(
                    'inline-flex items-center gap-1',
                    filterActive ? 'text-brand-600 hover:text-brand-700' : 'text-text-muted cursor-default',
                  )}
                  disabled={!filterActive}
                >
                  <X className="h-3 w-3" />
                  Clear
                </button>
              </div>

              {/* Checklist - flex-grows within the viewport-capped popover
                  so the whole box always fits and the list scrolls inside. */}
              <div className="flex-1 min-h-0 overflow-y-auto -mx-1">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-text-muted space-y-2">
                    <p>
                      {searchTerm.trim()
                        ? `No ${label.toLowerCase()} value here matches “${searchTerm.trim()}”.`
                        : `No ${label.toLowerCase()} values to filter by yet.`}
                    </p>
                    {searchTerm.trim() && onGlobalSearch ? (
                      <>
                        <p>
                          That box filters this list of {label.toLowerCase()} values, not the
                          orders themselves.
                        </p>
                        <button
                          type="button"
                          className="text-brand-600 hover:text-brand-700 underline text-start"
                          onClick={() => {
                            onGlobalSearch(searchTerm.trim());
                            setOpen(false);
                          }}
                        >
                          {`Search all orders for “${searchTerm.trim()}” instead`}
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <ul className="flex flex-col">
                    {filteredOptions.map((opt) => {
                      const checked = selected.includes(opt.value);
                      return (
                        <li key={opt.value}>
                          <label
                            className={cn(
                              'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer',
                              'hover:bg-sand-50',
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(opt.value)}
                              className="h-3.5 w-3.5 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
                            />
                            <span className="truncate text-text-primary">{opt.label}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {scopeNote ? (
                <p className="mt-1 px-2 pt-1 text-[11px] leading-snug text-text-muted border-t border-border-subtle shrink-0">
                  {scopeNote}
                </p>
              ) : null}
            </>
          ) : null}

          {!hasOptions ? (
            <div className="mt-2 px-2 py-1.5 text-xs text-text-muted">
              {sortOnlyHint ?? (
                <span className="flex items-center gap-1.5">
                  <Filter className="h-3 w-3" />
                  Filtering by value is not available for this column.
                </span>
              )}
            </div>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function SortButton({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 w-full text-sm rounded-md px-2 py-1.5',
        active
          ? 'bg-brand-50 text-brand-700'
          : 'text-text-primary hover:bg-sand-50',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
